import { useEffect, useMemo, useState } from 'react'
import ResellerShell from './ResellerShell'
import Toggle from '../../components/Toggle'
import { tournamentApi } from '../../services/api'
import { useLanguage } from '../../context/LanguageContext'
import { RESELLER_MENU_ITEMS, DEFAULT_HIDDEN_NAV } from '../../config/resellerMenu'

/**
 * Reseller Team management (owner-only). Invite users to your own account and
 * control which sidebar sections each can access. Access is stored as a set of
 * HIDDEN nav keys (menu items toggled OFF); the last set is remembered so the
 * next invite defaults to it.
 */
const SECTIONS = [
  { key: 'setup',   fallback: 'Setup' },
  { key: 'build',   fallback: 'Build' },
  { key: 'operate', fallback: 'Operate' },
]

export default function ResellerTeam() {
  const { t } = useLanguage()
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [msg, setMsg] = useState('')
  const [inviteOpen, setInviteOpen] = useState(false)
  const [editUser, setEditUser] = useState(null) // member being edited

  function load() {
    setLoading(true)
    tournamentApi.get('/portal/reseller/team')
      .then(r => { setData(r.data); setError(null) })
      .catch(e => setError(e?.response?.data?.message || e.message))
      .finally(() => setLoading(false))
  }
  useEffect(load, [])

  async function removeMember(m) {
    if (!confirm(`Remove ${m.firstName || m.email} from your team? They'll lose access immediately.`)) return
    try { await tournamentApi.delete(`/portal/reseller/team/${m.userId}`); setMsg(`Removed ${m.email}.`); load() }
    catch (e) { setMsg(`⚠ ${e?.response?.data?.message || e.message}`) }
  }
  async function revokeInvite(inv) {
    if (!confirm(`Revoke the pending invite to ${inv.email}?`)) return
    try { await tournamentApi.delete(`/portal/reseller/team/invite/${inv.inviteId}`); setMsg(`Revoked invite to ${inv.email}.`); load() }
    catch (e) { setMsg(`⚠ ${e?.response?.data?.message || e.message}`) }
  }

  const members = data?.members || []
  const pending = data?.pendingInvites || []
  const defaultHidden = data?.lastStaffNavDefaults?.length ? data.lastStaffNavDefaults : DEFAULT_HIDDEN_NAV

  return (
    <ResellerShell>
      <div style={{ maxWidth: 920, margin: '0 auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap', marginBottom: 8 }}>
          <div>
            <h1 style={{ margin: 0, fontSize: 24 }}>{t('sp_nav_team', 'Team')}</h1>
            <p style={{ color: '#64748b', marginTop: 6, marginBottom: 0, fontSize: 14, maxWidth: 640 }}>
              {t('sp_team_subtitle', 'Invite people to help run your account. Choose which sections each person can see — you can change it anytime.')}
            </p>
          </div>
          <button onClick={() => setInviteOpen(true)} style={primaryBtn}>{t('sp_team_invite_cta', '+ Invite member')}</button>
        </div>

        {error && <div style={errorBox}>{error}</div>}
        {msg && <div style={okBox}>{msg}</div>}

        {loading ? <div style={empty}>{t('loading_ellipsis', 'Loading…')}</div> : (
          <>
            <div style={card}>
              <table style={tbl}>
                <thead><tr style={tr}>
                  <th style={th}>{t('sp_team_col_member', 'Member')}</th>
                  <th style={th}>{t('sp_team_col_role', 'Role')}</th>
                  <th style={th}>{t('sp_team_col_access', 'Access')}</th>
                  <th style={{ ...th, textAlign: 'right' }}></th>
                </tr></thead>
                <tbody>
                  {members.map(m => (
                    <tr key={m.userId} style={tr}>
                      <td style={td}>
                        <strong>{[m.firstName, m.lastName].filter(Boolean).join(' ') || '—'}</strong>
                        <div style={{ color: '#64748b', fontSize: 12 }}>{m.email}</div>
                      </td>
                      <td style={td}>{m.isOwner
                        ? <span style={ownerBadge}>{t('sp_team_owner', 'Owner')}</span>
                        : <span style={staffBadge}>{t('sp_team_staff', 'Staff')}</span>}</td>
                      <td style={td}>{m.isOwner
                        ? <span style={{ color: '#64748b', fontSize: 13 }}>{t('sp_team_full_access', 'Full access')}</span>
                        : <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-start' }}>
                            <AccessSummary hidden={m.hiddenNavKeys} t={t} />
                            {m.permissions?.includes('issue_refunds') && (
                              <span style={refundChip}>↩ {t('sp_team_perm_refunds', 'Issue refunds')}{m.refundLimit != null ? ` ≤ $${Number(m.refundLimit).toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : ''}</span>
                            )}
                          </div>}</td>
                      <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                        {!m.isOwner && <>
                          <button onClick={() => setEditUser(m)} style={linkBtn}>{t('sp_team_edit_access', 'Edit access')}</button>
                          <button onClick={() => removeMember(m)} style={{ ...linkBtn, color: '#b91c1c' }}>{t('sp_remove', 'Remove')}</button>
                        </>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {pending.length > 0 && (
              <>
                <h3 style={{ fontSize: 14, color: '#475569', margin: '22px 0 8px' }}>{t('sp_team_pending', 'Pending invites')}</h3>
                <div style={card}>
                  <table style={tbl}><tbody>
                    {pending.map(inv => (
                      <tr key={inv.inviteId} style={tr}>
                        <td style={td}>
                          <strong>{[inv.firstName, inv.lastName].filter(Boolean).join(' ') || inv.email}</strong>
                          <div style={{ color: '#64748b', fontSize: 12 }}>{inv.email}</div>
                        </td>
                        <td style={td}><span style={pendBadge}>{t('sp_team_invite_pending', 'Invited')}</span></td>
                        <td style={{ ...td, textAlign: 'right' }}>
                          <button onClick={() => revokeInvite(inv)} style={{ ...linkBtn, color: '#b91c1c' }}>{t('sp_team_revoke', 'Revoke')}</button>
                        </td>
                      </tr>
                    ))}
                  </tbody></table>
                </div>
              </>
            )}
          </>
        )}
      </div>

      {inviteOpen && (
        <MemberModal
          mode="invite"
          initialHidden={defaultHidden}
          initialPermissions={[]}
          initialRefundLimit={null}
          onClose={() => setInviteOpen(false)}
          onSaved={(m) => { setInviteOpen(false); setMsg(m); load() }}
        />
      )}
      {editUser && (
        <MemberModal
          mode="edit"
          member={editUser}
          initialHidden={editUser.hiddenNavKeys || []}
          initialPermissions={editUser.permissions || []}
          initialRefundLimit={editUser.refundLimit}
          onClose={() => setEditUser(null)}
          onSaved={(m) => { setEditUser(null); setMsg(m); load() }}
        />
      )}
    </ResellerShell>
  )
}

function AccessSummary({ hidden, t }) {
  const total = RESELLER_MENU_ITEMS.length
  const allowed = total - (hidden?.length || 0)
  return <span style={{ color: '#64748b', fontSize: 13 }}>{t('sp_team_access_count', '{n} of {total} sections', { n: allowed, total })}</span>
}

/**
 * Invite / edit modal. Renders the menu catalog grouped by section with a
 * toggle per item = "can this person access it". Stores hidden = items OFF.
 */
function MemberModal({ mode, member, initialHidden, initialPermissions, initialRefundLimit, onClose, onSaved }) {
  const { t } = useLanguage()
  const [firstName, setFirstName] = useState(member?.firstName || '')
  const [lastName, setLastName] = useState(member?.lastName || '')
  const [email, setEmail] = useState(member?.email || '')
  const [hidden, setHidden] = useState(() => new Set(initialHidden || []))
  const [permissions, setPermissions] = useState(() => new Set(initialPermissions || []))
  const [refundLimit, setRefundLimit] = useState(initialRefundLimit != null ? String(initialRefundLimit) : '')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState(null)

  function togglePermission(key, on) {
    setPermissions(prev => {
      const next = new Set(prev)
      if (on) next.add(key); else next.delete(key)
      return next
    })
  }
  // When "Issue refunds" is switched on, seed the ceiling to $1,000 so there's
  // always a sensible default the owner can then adjust (guardrail #236).
  function toggleIssueRefunds(on) {
    togglePermission('issue_refunds', on)
    if (on && (refundLimit === '' || refundLimit == null)) setRefundLimit('1000')
  }

  const bySection = useMemo(() => {
    const g = {}
    for (const it of RESELLER_MENU_ITEMS) { (g[it.section] ||= []).push(it) }
    return g
  }, [])

  function toggleAccess(key, canAccess) {
    setHidden(prev => {
      const next = new Set(prev)
      if (canAccess) next.delete(key); else next.add(key)
      return next
    })
  }

  async function submit() {
    setBusy(true); setErr(null)
    const hiddenNavKeys = [...hidden]
    const perms = [...permissions]
    // Only send a limit alongside the grant; blank = unlimited (null).
    const limit = perms.includes('issue_refunds') ? (refundLimit === '' ? null : Number(refundLimit)) : null
    try {
      if (mode === 'invite') {
        const { data } = await tournamentApi.post('/portal/reseller/team/invite', { firstName, lastName, email, hiddenNavKeys, permissions: perms, refundLimit: limit })
        onSaved(data?.emailSent ? `✓ Invitation sent to ${email}.` : `Invite created, but the email failed: ${data?.emailError || 'unknown'}.`)
      } else {
        await tournamentApi.patch(`/portal/reseller/team/${member.userId}`, { hiddenNavKeys, permissions: perms, refundLimit: limit })
        onSaved(`✓ Updated ${member.email}'s access.`)
      }
    } catch (e) { setErr(e?.response?.data?.message || e.message) }
    finally { setBusy(false) }
  }

  const canSubmit = mode === 'edit' || (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) && !busy)

  return (
    <div style={overlay} onClick={busy ? undefined : onClose}>
      <div style={modal} onClick={e => e.stopPropagation()}>
        <h2 style={{ margin: '0 0 4px', fontSize: 18 }}>
          {mode === 'invite' ? t('sp_team_invite_title', 'Invite a team member') : t('sp_team_edit_title', 'Edit access')}
        </h2>
        <p style={{ color: '#64748b', fontSize: 13, marginTop: 0 }}>
          {mode === 'invite'
            ? t('sp_team_invite_help', "They'll get an email to set a password, then can sign in to the sections you allow below.")
            : <>{member?.email}</>}
        </p>

        {mode === 'invite' && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
            <label style={lbl}>{t('first_name', 'First name')}
              <input value={firstName} onChange={e => setFirstName(e.target.value)} style={inp} />
            </label>
            <label style={lbl}>{t('last_name', 'Last name')}
              <input value={lastName} onChange={e => setLastName(e.target.value)} style={inp} />
            </label>
            <label style={{ ...lbl, gridColumn: '1 / -1' }}>{t('email', 'Email')}
              <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="name@example.com" style={inp} />
            </label>
          </div>
        )}

        <div style={{ fontSize: 12, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: 0.5, margin: '8px 0 6px' }}>
          {t('sp_team_access_heading', 'What can they access?')}
        </div>
        <div style={{ maxHeight: 320, overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: 8, padding: 12 }}>
          {SECTIONS.map(sec => (
            <div key={sec.key} style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>
                {t('sp_nav_section_' + sec.key, sec.fallback)}
              </div>
              {(bySection[sec.key] || []).map(it => (
                <div key={it.key} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '5px 0' }}>
                  <Toggle checked={!hidden.has(it.key)} onChange={(v) => toggleAccess(it.key, v)} />
                  <span style={{ fontSize: 14, color: '#0f172a' }}>{t(it.labelKey, it.fallback)}</span>
                </div>
              ))}
            </div>
          ))}
        </div>

        <div style={{ fontSize: 12, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: 0.5, margin: '16px 0 6px' }}>
          {t('sp_team_permissions_label', 'Permissions')}
        </div>
        <div style={{ border: '1px solid #e2e8f0', borderRadius: 8, padding: 12 }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
            <Toggle checked={permissions.has('issue_refunds')} onChange={toggleIssueRefunds} />
            <div>
              <div style={{ fontSize: 14, color: '#0f172a', fontWeight: 600 }}>{t('sp_team_perm_refunds', 'Issue refunds')}</div>
              <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
                {t('sp_team_perm_refunds_help', 'Can issue partial refunds and cancel-and-refund on bookings and custom trips, and work the in-progress refund steps.')}
              </div>
            </div>
          </div>
          {permissions.has('issue_refunds') && (
            <div style={{ marginTop: 12, paddingLeft: 44 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 4 }}>{t('sp_team_refund_limit_label', 'Refund limit')}</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ color: '#64748b', fontSize: 14 }}>$</span>
                <input type="number" min="0" step="50" value={refundLimit}
                  onChange={e => setRefundLimit(e.target.value)}
                  placeholder={t('sp_team_refund_limit_unlimited', 'No limit')}
                  style={{ ...inp, width: 150, marginTop: 0 }} />
              </div>
              <div style={{ fontSize: 11.5, color: '#64748b', marginTop: 4, maxWidth: 380 }}>
                {t('sp_team_refund_limit_help', 'Refunds up to this amount are issued right away. Anything above it needs your approval first.')}
              </div>
            </div>
          )}
        </div>

        {err && <div style={{ ...errorBox, marginTop: 10, marginBottom: 0 }}>{err}</div>}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16 }}>
          <button onClick={onClose} disabled={busy} style={ghostBtn}>{t('cancel', 'Cancel')}</button>
          <button onClick={submit} disabled={!canSubmit} style={{ ...primaryBtn, opacity: canSubmit ? 1 : 0.5, cursor: canSubmit ? 'pointer' : 'not-allowed' }}>
            {busy ? t('saving_ellipsis', 'Saving…') : (mode === 'invite' ? t('sp_team_send_invite', 'Send invite') : t('save', 'Save'))}
          </button>
        </div>
      </div>
    </div>
  )
}

const card = { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden' }
const tbl = { width: '100%', borderCollapse: 'collapse', fontSize: 14 }
const tr = { borderBottom: '1px solid #f1f5f9' }
const th = { padding: '10px 14px', textAlign: 'left', fontWeight: 600, color: '#64748b', fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.4, background: '#f8fafc' }
const td = { padding: '12px 14px', verticalAlign: 'middle' }
const empty = { padding: 40, textAlign: 'center', color: '#64748b', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8 }
const errorBox = { padding: 12, background: '#fef2f2', color: '#991b1b', border: '1px solid #fecaca', borderRadius: 6, marginBottom: 14 }
const okBox = { padding: '8px 12px', background: '#f0fdf4', border: '1px solid #bbf7d0', color: '#166534', borderRadius: 6, marginBottom: 14, fontSize: 13 }
const ownerBadge = { padding: '2px 8px', background: '#dcfce7', color: '#166534', borderRadius: 999, fontSize: 11, fontWeight: 700 }
const staffBadge = { padding: '2px 8px', background: '#e0e7ff', color: '#3730a3', borderRadius: 999, fontSize: 11, fontWeight: 700 }
const pendBadge = { padding: '2px 8px', background: '#fef3c7', color: '#92400e', borderRadius: 999, fontSize: 11, fontWeight: 700 }
const refundChip = { padding: '1px 8px', background: '#fef9c3', color: '#a16207', borderRadius: 999, fontSize: 11, fontWeight: 700 }
const linkBtn = { background: 'none', border: 'none', color: 'var(--brand-600, #0f766e)', cursor: 'pointer', fontSize: 13, fontWeight: 600, padding: '4px 8px' }
const primaryBtn = { padding: '9px 16px', background: '#0f172a', color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: 'pointer' }
const ghostBtn = { padding: '9px 16px', background: '#fff', color: '#475569', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' }
const overlay = { position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }
const modal = { background: '#fff', borderRadius: 12, padding: 22, width: '100%', maxWidth: 480, maxHeight: '92vh', overflowY: 'auto', boxShadow: '0 20px 40px rgba(0,0,0,0.2)' }
const lbl = { display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, fontWeight: 600, color: '#475569' }
const inp = { padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 14, marginTop: 2 }
